import React, { useState, useEffect } from 'react';
import { Box } from 'reflexbox';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import styled from 'styled-components';
import { SubHeader, HealtheraButton } from '../../../healtheraComponents';
import Switch from '../../Shared/Switch';
import { useAlphegaMembership, useUpdateAlphegaMembership } from './hooks/useAlphegaMembership';

const CONNECT_V1_URL = process.env.REACT_APP_CONNECT_V1_URL;

const ExternalLink = styled.a`
  color: #2f9d6d;
  font-size: 14px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const InfoIcon = styled(InfoOutlinedIcon)`
  color: #757575;
  font-size: 18px;
  margin-left: 8px;
  cursor: help;
  vertical-align: middle;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
`;

type AlphegaMemberSettingsProps = {
  pharmacyId: string;
};

export default function AlphegaMemberSettings({ pharmacyId }: AlphegaMemberSettingsProps) {
  const { data: membership, isLoading, error } = useAlphegaMembership(pharmacyId);
  const { mutate: updateMembership, isLoading: isUpdating } = useUpdateAlphegaMembership(pharmacyId);

  const [isMember, setIsMember] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (membership) {
      const memberStatus = !!membership.membership_id;
      setIsMember(memberStatus);
      setAccountNumber(membership.alphega_account_number || '');
      setHasChanges(false);
    }
  }, [membership]);

  const handleToggleMembership = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setIsMember(isChecked);
    setHasChanges(true);

    if (!isChecked) {
      // Turning off membership clears account number
      setAccountNumber('');
    }
  };

  const handleAccountNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAccountNumber(event.target.value);
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMembership({
      is_member: isMember,
      alphega_account_number: isMember ? accountNumber || null : null,
    });
    setHasChanges(false);
  };

  if (isLoading) {
    return (
      <>
        <Box>
          <SubHeader title="Alphega Member Settings" />
        </Box>
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={24} />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Box>
          <SubHeader title="Alphega Member Settings" />
        </Box>
        <Box p={2} color="error.main">
          Failed to load Alphega membership settings
        </Box>
      </>
    );
  }

  return (
    <>
      <Box>
        <SubHeader title="Alphega Member Settings" />
      </Box>
      <Box ml={2}>
        <Box mb={2}>
          <ExternalLink
            href={`${CONNECT_V1_URL}/pharmacies/${pharmacyId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Update opening hours on Connect →
          </ExternalLink>
        </Box>
        <SwitchRow>
          <Switch
            label="Alphega Member"
            value={isMember}
            onChange={handleToggleMembership}
            checked={isMember}
            disabled={isUpdating}
            reverseOrder={false}
          />
          <Tooltip
            title="Enabling this will make this pharmacy searchable on the Alphega website pharmacy finder"
            placement="right"
          >
            <InfoIcon />
          </Tooltip>
        </SwitchRow>

        {isMember && (
          <Box mt={1}>
            <TextField
              label="Alphega Account Number"
              value={accountNumber}
              onChange={handleAccountNumberChange}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={isUpdating}
              placeholder="Enter account number"
            />
          </Box>
        )}

        {hasChanges && (
          <Box mt={2}>
            <HealtheraButton
              width="95px"
              onClick={handleSave}
              disabled={isUpdating}
            >
              {isUpdating ? <CircularProgress size={20} color="inherit" /> : 'Save'}
            </HealtheraButton>
          </Box>
        )}
      </Box>
    </>
  );
}
